import apiClient from './apiClient';

// Enhanced Source interface for compatibility with main app
export interface EnhancedSource {
  type: 'paragraph' | 'judgment' | 'document';
  reference: string;
  title: string;
  risLink?: string;
  found: boolean;
  documentId?: string;
  confidence?: number;
}

// Enhanced Chat Session interface matching main app
export interface EnhancedChatSession {
  id: number;
  sessionId: string;
  title: string;
  serviceType: 'ris' | 'clairis' | 'berufung' | 'multiagent';
  createdAt: Date;
  updatedAt: Date;
  lastMessageAt: Date;
  messageCount: number;
  isActive: boolean;
  previewText?: string;
  chatType?: 'simple' | 'advanced' | 'multi-agent' | 'ris';
  fileInfo?: {
    originalName: string;
    geminiFileUri?: string;
    extractedTextLength?: number;
  };
}

// Enhanced Chat Message interface matching main app
export interface EnhancedChatMessage {
  id: number;
  sessionId: string;
  role: 'user' | 'model';
  content: string;
  timestamp: Date;
  metadata?: {
    agentName?: string;
    contentLength?: number;
    fileReference?: string;
    sources?: string[];
    verifiedSources?: EnhancedSource[];
    sourceVerificationStatus?: 'pending' | 'completed' | 'failed';
    messageId?: string;
    serviceType?: 'ris' | 'berufung' | 'multiagent';
    messageIndex?: number;
    risLinkMetadata?: any;
  };
}

// Legacy interfaces for backward compatibility
export interface ChatSession {
  id: string;
  sessionId: string;
  serviceType: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  lastMessageAt: string | null;
  isActive: boolean;
}

export interface ChatMessage {
  id: string;
  messageId: string;
  role: 'user' | 'assistant';
  content: string;
  mode?: string;
  sources?: any[];
  verifiedSources?: any;
  qualityScore?: number;
  createdAt: string;
}

export interface ChatSessionWithMessages extends ChatSession {
  messages: ChatMessage[];
}

export const SERVICE_TYPE_CONFIG = {
  ris: {
    label: 'RISearch 2.0',
    icon: '🔍',
    description: 'KI-gestützte Rechtsrecherche',
    supportsSourceVerification: true,
    maxSessions: 50
  },
  clairis: {
    label: 'RISearch Chat',
    icon: '💬',
    description: 'Rechtlicher KI-Assistent',
    supportsSourceVerification: false,
    maxSessions: 50
  },
  berufung: {
    label: 'Berufungsassistent',
    icon: '📝',
    description: 'Berufungsschreiben Generator',
    supportsSourceVerification: false,
    maxSessions: 30
  },
  multiagent: {
    label: 'Multi-Agent System',
    icon: '🤖',
    description: 'Kollaborative KI-Agenten',
    supportsSourceVerification: false,
    maxSessions: 20
  }
};

class WebChatHistoryService {
  // Helper to convert API response to EnhancedChatSession
  private mapToEnhancedSession(session: any): EnhancedChatSession {
    return {
      id: typeof session.id === 'string' ? parseInt(session.id, 10) : session.id,
      sessionId: session.sessionId,
      title: session.title || 'Unbenannte Session',
      serviceType: (session.serviceType || 'ris') as 'ris' | 'berufung' | 'multiagent',
      createdAt: new Date(session.createdAt),
      updatedAt: new Date(session.updatedAt),
      lastMessageAt: new Date(session.lastMessageAt || session.updatedAt),
      messageCount: session.messageCount || 0,
      isActive: session.isActive !== false,
      previewText: session.previewText
    };
  }

  // Helper to convert API response to EnhancedChatMessage
  private mapToEnhancedMessage(msg: any): EnhancedChatMessage {
    return {
      id: typeof msg.id === 'string' ? parseInt(msg.id, 10) : msg.id,
      sessionId: msg.sessionId,
      role: msg.role === 'assistant' ? 'model' : 'user',
      content: msg.content,
      timestamp: new Date(msg.createdAt || msg.timestamp),
      metadata: {
        sources: msg.sources,
        verifiedSources: msg.verifiedSources,
        sourceVerificationStatus: msg.verifiedSources ? 'completed' : undefined,
        messageId: msg.messageId,
        serviceType: 'ris'
      }
    };
  }

  // Generate a session ID (client-side for compatibility)
  async generateSessionId(serviceType: string, userId?: number): Promise<string> {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    const userPrefix = userId ? `${userId}_` : '';
    return `${userPrefix}${serviceType}_${timestamp}_${random}`;
  }

  // Get sessions by service type - returns EnhancedChatSession[]
  async getSessionsByServiceType(
    serviceType: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<EnhancedChatSession[]> {
    try {
      const response = await apiClient.get('/api/ris/sessions', {
        params: { serviceType, limit, offset },
      });

      const sessions = response.data.sessions || response.data || [];
      return sessions.map((s: any) => this.mapToEnhancedSession(s));
    } catch (error) {
      console.error('Error fetching sessions by service type:', error);
      return [];
    }
  }

  // Get a specific chat session with messages
  async getChatSession(sessionId: string): Promise<{ session: EnhancedChatSession; messages: EnhancedChatMessage[] }> {
    try {
      const response = await apiClient.get(`/api/ris/sessions/${sessionId}`);
      const data = response.data;

      const session = this.mapToEnhancedSession(data.session || data);
      const messages = (data.messages || []).map((m: any) => this.mapToEnhancedMessage(m));

      return { session, messages };
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Session not found');
      }
      throw error;
    }
  }

  // Create a new chat session (V2 API)
  async createChatSessionV2(data: {
    sessionId: string;
    title: string;
    serviceType: 'ris' | 'berufung' | 'multiagent';
    fileInfo?: {
      originalName: string;
      geminiFileUri?: string;
      extractedTextLength?: number;
    };
  }): Promise<EnhancedChatSession> {
    try {
      const response = await apiClient.post('/api/ris/sessions', {
        sessionId: data.sessionId,
        serviceType: data.serviceType,
        title: data.title
      });
      return this.mapToEnhancedSession(response.data.session || response.data);
    } catch (error) {
      console.error('Error creating chat session:', error);
      throw error;
    }
  }

  // Legacy create method
  async createChatSession(data: {
    sessionId: string;
    serviceType: string;
    title?: string;
  }): Promise<ChatSession> {
    const response = await apiClient.post('/api/ris/sessions', data);
    return response.data.session;
  }

  // Update session title
  async updateSessionTitle(sessionId: string, title: string): Promise<void> {
    await apiClient.patch(`/api/ris/sessions/${sessionId}`, { title });
  }

  // Deactivate (soft delete) a session
  async deactivateSession(sessionId: string): Promise<void> {
    await apiClient.delete(`/api/ris/sessions/${sessionId}`);
  }

  // Save a user message (legacy)
  async saveUserMessage(data: {
    sessionId: string;
    messageId: string;
    content: string;
    mode?: string;
  }): Promise<ChatMessage> {
    const response = await apiClient.post(`/api/ris/sessions/${data.sessionId}/messages`, {
      messageId: data.messageId,
      role: 'user',
      content: data.content,
      mode: data.mode,
    });
    return response.data.message;
  }

  // Save an assistant message (legacy)
  async saveAssistantMessage(data: {
    sessionId: string;
    messageId: string;
    content: string;
    mode?: string;
    sources?: any[];
    verifiedSources?: any;
    qualityScore?: number;
  }): Promise<ChatMessage> {
    const response = await apiClient.post(`/api/ris/sessions/${data.sessionId}/messages`, {
      messageId: data.messageId,
      role: 'assistant',
      content: data.content,
      mode: data.mode,
      sources: data.sources,
      verifiedSources: data.verifiedSources,
      qualityScore: data.qualityScore,
    });
    return response.data.message;
  }

  // Save RIS user message (enhanced API matching main app)
  async saveRISUserMessage(sessionId: string, content: string, messageId?: string): Promise<EnhancedChatMessage> {
    const msgId = messageId || `user_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;

    try {
      const response = await apiClient.post(`/api/ris/sessions/${sessionId}/messages`, {
        messageId: msgId,
        role: 'user',
        content: content
      });
      return this.mapToEnhancedMessage(response.data.message || response.data);
    } catch (error) {
      console.error('Error saving RIS user message:', error);
      // Return a local message object if API fails
      return {
        id: Date.now(),
        sessionId,
        role: 'user',
        content,
        timestamp: new Date(),
        metadata: { messageId: msgId, serviceType: 'ris' }
      };
    }
  }

  // Save RIS assistant message (enhanced API matching main app)
  async saveRISAssistantMessage(
    sessionId: string,
    content: string,
    messageId?: string,
    sources?: string[],
    verificationStatus: 'pending' | 'completed' | 'failed' = 'completed',
    verifiedSources?: EnhancedSource[]
  ): Promise<EnhancedChatMessage> {
    const msgId = messageId || `assistant_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;

    console.log(`[WebChatHistoryService] Saving RIS assistant message with messageId: ${msgId}`);

    try {
      const response = await apiClient.post(`/api/ris/sessions/${sessionId}/messages`, {
        messageId: msgId,
        role: 'assistant',
        content: content,
        sources: sources,
        verifiedSources: verifiedSources
      });
      return this.mapToEnhancedMessage(response.data.message || response.data);
    } catch (error) {
      console.error('Error saving RIS assistant message:', error);
      // Return a local message object if API fails
      return {
        id: Date.now(),
        sessionId,
        role: 'model',
        content,
        timestamp: new Date(),
        metadata: {
          messageId: msgId,
          serviceType: 'ris',
          sources,
          verifiedSources,
          sourceVerificationStatus: verificationStatus
        }
      };
    }
  }

  // Search chat sessions
  async searchChatSessions(
    query: string,
    limit: number = 20
  ): Promise<ChatSession[]> {
    try {
      const response = await apiClient.get('/api/ris/sessions/search', {
        params: { q: query, limit },
      });
      return response.data.sessions || [];
    } catch (error) {
      console.error('Error searching chat sessions:', error);
      return [];
    }
  }

  // Get chat statistics
  async getChatStats(): Promise<{
    totalSessions: number;
    totalMessages: number;
    sessionsByType: Record<string, number>;
  }> {
    try {
      const response = await apiClient.get('/api/ris/sessions/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching chat stats:', error);
      return { totalSessions: 0, totalMessages: 0, sessionsByType: {} };
    }
  }

  // Get total session count
  async getTotalSessionCount(serviceType?: string): Promise<number> {
    try {
      const stats = await this.getChatStats();
      if (serviceType) {
        return stats.sessionsByType[serviceType] || 0;
      }
      return stats.totalSessions;
    } catch (error) {
      return 0;
    }
  }

  // Static utility methods for formatting
  static formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch {
      return 'Ungueltiges Datum';
    }
  }

  static formatPreview(content: string, maxLength: number = 100): string {
    if (!content) return '';

    const cleanContent = content
      .replace(/[#*`_~]/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    if (cleanContent.length <= maxLength) {
      return cleanContent;
    }

    return cleanContent.substring(0, maxLength) + '...';
  }

  static getServiceTypeLabel(serviceType: 'ris' | 'clairis' | 'berufung' | 'multiagent'): string {
    return SERVICE_TYPE_CONFIG[serviceType]?.label || serviceType;
  }

  static getServiceTypeIcon(serviceType: 'ris' | 'clairis' | 'berufung' | 'multiagent'): string {
    return SERVICE_TYPE_CONFIG[serviceType]?.icon || '?';
  }

  // Instance methods for convenience
  formatDate(dateString: string): string {
    return WebChatHistoryService.formatDate(dateString);
  }

  formatPreview(content: string, maxLength: number = 100): string {
    return WebChatHistoryService.formatPreview(content, maxLength);
  }

  getServiceTypeLabel(serviceType: 'ris' | 'clairis' | 'berufung' | 'multiagent'): string {
    return WebChatHistoryService.getServiceTypeLabel(serviceType);
  }

  getServiceTypeIcon(serviceType: 'ris' | 'clairis' | 'berufung' | 'multiagent'): string {
    return WebChatHistoryService.getServiceTypeIcon(serviceType);
  }
}

export const webChatHistoryService = new WebChatHistoryService();
export default webChatHistoryService;
